import React, { useEffect, useState } from 'react';
import {
    Box, Grid, TextField, Button, Typography, Select, MenuItem,
    InputLabel, FormControl, Table, TableHead, TableRow,
    TableCell, TableBody, IconButton, Card, Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { auth, db } from '../../firebase';
import {
    collection, doc, getDoc, updateDoc, addDoc, deleteDoc,
    query, orderBy, onSnapshot, where, serverTimestamp,
    getDocs
} from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import Shopheader from '../Shop/Shopheader';
import Mashalheader from '../Mashal/Mashalheader';
import Wholesalerheader from '../Wholesale/Wholesaleheader';

export default function EditSale() {
    const { category, saleId } = useParams();
    const navigate = useNavigate();

    const [userData, setUserData] = useState(null);
    const [dealers, setDealers] = useState([]);
    const [products, setProducts] = useState([]);
    const [saleItems, setsaleItems] = useState([]);

    const [customerId, setcustomerId] = useState('');
    const [SaleDate, setSaleDate] = useState('');
    const [paytype, setPaytype] = useState('Cash');

    const [itemId, setItemId] = useState('');
    const [qty, setQty] = useState(1);
    const [rate, setRate] = useState('');
    const [discount, setDiscount] = useState(0);

    const [subtotal, setSubtotal] = useState('');
    const [totalAmount, setTotalAmount] = useState('');
    const [grandTotal, setGrandTotal] = useState(0);
    const [paidAmount, setPaidAmount] = useState(0);
    const [remaining, setRemaining] = useState(0);

    // ---------------- AUTH ----------------
    useEffect(() => {
        auth.onAuthStateChanged((user) => {
            setUserData(user);
        });
    }, []);

    // ---------------- FETCH sale ----------------
    useEffect(() => {
        fetchProductsAndcustomers();
        fetchsaleData();
    }, [category, saleId]);

    const fetchProductsAndcustomers = async () => {
        try {
            const qcustomers = query(collection(db, 'customers'), where('category', '==', category));
            onSnapshot(qcustomers, (snap) => {
                setDealers(snap.docs.map((doc) => ({ id: doc.id, data: doc.data() })));
            });

            const qProducts = query(
                collection(db, 'products'),
                where('category', '==', category),
                orderBy('productname', 'asc')
            );
            onSnapshot(qProducts, (snap) => {
                setProducts(snap.docs.map((doc) => ({ id: doc.id, data: doc.data() })));
            });
        } catch (error) {
            toast.error(error.message);
        }
    };

    const fetchsaleData = async () => {
        try {
            const saleRef = doc(db, 'sales', saleId);
            const snap = await getDoc(saleRef);

            if (snap.exists()) {
                const data = snap.data();
                setcustomerId(data.customerId);
                setSaleDate(data.SaleDate);
                setPaytype(data.paytype);
                setGrandTotal(data.grandTotal || 0);
                setPaidAmount(data.paidAmount || 0);
                setRemaining(data.remaining || 0);
            }

            // Fetch sale items
            const qItems = query(
                collection(db, 'salesitems'),
                where('saleid', '==', saleId)
            );
            onSnapshot(qItems, (snap) => {
                const items = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                setsaleItems(items);
                updateGrandTotal(items);
            });
        } catch (error) {
            toast.error(error.message);
        }
    };

    // ---------------- CALCULATIONS ----------------
    const calculateItemTotal = (r, q, d) => {
        const subtotal = parseFloat(r || 0) * parseFloat(q || 0) - parseFloat(d || 0);
        return parseFloat(subtotal.toFixed(2));
    };

    const updateGrandTotal = (items) => {
        const total = items.reduce((acc, item) => acc + parseFloat(item.totalAmount || 0), 0);
        setGrandTotal(total.toFixed(2));
        setRemaining((total - parseFloat(paidAmount || 0)).toFixed(2));
    };

    // ---------------- HANDLE PRODUCT SELECT ----------------
    const handleProductSelect = (prodId) => {
        setItemId(prodId);
        const selected = products.find((p) => p.id === prodId);
        if (selected) {
            const r = parseFloat(selected.data.salerate || 0);
            setRate(r);
            const subtotal = calculateItemTotal(r, qty, discount);
            setSubtotal(subtotal);
            setTotalAmount(subtotal);
        }
    };

    const handleQtyOrDiscountChange = (newQty, newDiscount, newRate) => {
        const subtotal = calculateItemTotal(newRate, newQty, newDiscount);
        setSubtotal(subtotal);
        setTotalAmount(subtotal);
    };

    // ---------------- ADD ITEM ----------------
    const addsaleItems = async () => {
        if (!itemId || !rate || !qty) {
            toast.error('Please fill required fields!');
            return;
        }

        const newItem = {
            saleid: saleId,
            itemId,
            qty: parseFloat(qty),
            rate: parseFloat(rate),
            discount: parseFloat(discount),
            totalAmount: parseFloat(totalAmount),
            created: serverTimestamp()
        };

        try {
            await addDoc(collection(db, 'salesitems'), newItem);
            toast.success('Item added successfully');

            // Reset input
            setItemId('');
            setQty(1);
            setRate('');
            setDiscount(0);
            setSubtotal('');
            setTotalAmount('');
        } catch (error) {
            toast.error(error.message);
        }
    };

    // ---------------- DELETE ITEM ----------------
    const deleteItem = async (id) => {
        try {
            await deleteDoc(doc(db, 'salesitems', id));
            toast.success('Item deleted successfully');
        } catch (error) {
            toast.error(error.message);
        }
    };

    // ---------------- UPDATE sale ----------------
    const updatesale = async () => {
        if (!customerId || !SaleDate || saleItems.length === 0) {
            toast.error('Please select Customer, date and add items!');
            return;
        }

        try {
            const saleRef = doc(db, 'sales', saleId);
            await updateDoc(saleRef, {
                customerId,
                SaleDate,
                grandTotal: parseFloat(grandTotal),
                paidAmount: parseFloat(paidAmount),
                remaining: parseFloat(remaining),
                paytype,
                category: category,
                updated: serverTimestamp(),
            });

            // Save payment history entry
            const paymentRef = collection(db, "customerpaymenthistory");
            const q = query(paymentRef, where("saleId", "==", saleId));
            const snap = await getDocs(q);

            if (!snap.empty) {
                // ✅ Update existing record
                const existingDoc = snap.docs[0];
                await updateDoc(doc(db, "customerpaymenthistory", existingDoc.id), {
                    customerId,
                    saleId,
                    amount: parseFloat(paidAmount),
                    remaining: parseFloat(remaining),
                    grandTotal: parseFloat(grandTotal),
                    paymentDate: SaleDate,
                    paytype,
                    updatedAt: serverTimestamp(),
                });
            } else {
                // ✅ Add new record if not exists
                await addDoc(paymentRef, {
                    customerId,
                    saleId,
                    amount: parseFloat(paidAmount),
                    remaining: parseFloat(remaining),
                    grandTotal: parseFloat(grandTotal),
                    paymentDate: SaleDate,
                    paytype,
                    createdAt: serverTimestamp(),
                });
            }

            toast.success('sale and payment updated successfully');
            navigate(`/view-sale/${category}`);
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <Box p={2} mt={10}>
            {category === "shop" && <Shopheader />}
            {category === "mashal" && <Mashalheader />}
            {category === "wholesaler" && <Wholesalerheader />}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Button variant="contained" onClick={() => navigate(`/view-sale/${category}`)}>
                    विक्री बघा
                </Button>
                <Typography variant="h5" fontWeight="bold">Edit विक्री </Typography>
            </Stack>

            <Card sx={{ p: 2 }}>
                <Grid className="row" >
                    <Grid className="col-sm-3 mt-3" item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>ग्राहक </InputLabel>
                            <Select value={customerId} onChange={(e) => setcustomerId(e.target.value)}>
                                {dealers.map(d => (
                                    <MenuItem key={d.id} value={d.id}>{d.data.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid className="col-sm-3 mt-3" item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label="विक्री दिनांक "
                            InputLabelProps={{ shrink: true }}
                            value={SaleDate}
                            onChange={(e) => setSaleDate(e.target.value)}
                            size="small"
                        />
                    </Grid>
                </Grid>

                {/* ---------------- ITEM TABLE ---------------- */}
                <Box mt={2} sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>प्रोडक्ट </TableCell>
                                <TableCell>रेट </TableCell>
                                <TableCell>नग </TableCell>
                                <TableCell>डिस्काउंट </TableCell>
                                <TableCell>टोटल </TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>
                                    <FormControl fullWidth size="small">
                                        <Select value={itemId} onChange={(e) => handleProductSelect(e.target.value)}>
                                            {products.map(p => (
                                                <MenuItem key={p.id} value={p.id}>{p.data.productname}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        value={rate}
                                        size="small"
                                        onChange={(e) => {
                                            const newRate = e.target.value;
                                            setRate(newRate);
                                            handleQtyOrDiscountChange(qty, discount, newRate);
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        value={qty}
                                        size="small"
                                        onChange={(e) => {
                                            const newQty = e.target.value;
                                            setQty(newQty);
                                            handleQtyOrDiscountChange(newQty, discount, rate);
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        value={discount}
                                        size="small"
                                        onChange={(e) => {
                                            const newDiscount = e.target.value;
                                            setDiscount(newDiscount);
                                            handleQtyOrDiscountChange(qty, newDiscount, rate);
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField value={totalAmount} readOnly size="small" />
                                </TableCell>
                                <TableCell>
                                    <IconButton color="primary" onClick={addsaleItems}>
                                        <AddIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>

                            {saleItems.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{products.find(p => p.id === item.itemId)?.data.productname || item.itemId}</TableCell>
                                    <TableCell>{item.rate}</TableCell>
                                    <TableCell>{item.qty}</TableCell>
                                    <TableCell>{item.discount}</TableCell>
                                    <TableCell>{item.totalAmount}</TableCell>
                                    <TableCell>
                                        <IconButton color="error" onClick={() => deleteItem(item.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                {/* ---------------- GRAND TOTALS ---------------- */}
                <Box mt={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField label="ग्रँड टोटल" fullWidth value={grandTotal} InputProps={{ readOnly: true }} size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                label="जमा रक्कम "
                                fullWidth
                                value={paidAmount}
                                onChange={(e) => {
                                    setPaidAmount(e.target.value);
                                    setRemaining((grandTotal - e.target.value).toFixed(2));
                                }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>पेमेंट टाईप </InputLabel>
                                <Select value={paytype} onChange={(e) => setPaytype(e.target.value)}>
                                    <MenuItem value="Cash">Cash</MenuItem>
                                    <MenuItem value="Online Pay">Online Pay</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField label="बाकी" fullWidth value={remaining} InputProps={{ readOnly: true }} size="small" />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={2} display="flex" justifyContent="center">
                    <Button variant="contained" color="primary" onClick={updatesale}>
                        Update Sale
                    </Button>
                </Box>
            </Card>
        </Box>
    );
}
